
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ListChecks, BarChart, Users, Building, ChevronLeft, UserCog, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import Logo from '@/icons/Logo';
import { Sidebar, SidebarContent, SidebarHeader, SidebarFooter, useSidebar } from '@/components/ui/sidebar';
import { UserProfileSection } from '@/components/admin/UserProfileSection';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarLink {
  name: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

export default function AdminSidebar() {
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const { isPlatformAdmin, isOrgAdmin } = useAuth();
  const isCollapsed = state === "collapsed";

  const links: SidebarLink[] = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard
    },
    {
      name: 'Assessments',
      href: '/admin/assessments',
      icon: ListChecks
    },
    {
      name: 'Results',
      href: '/admin/results',
      icon: BarChart
    },
    // For platform admins: show both platform users and organization users
    ...(isPlatformAdmin ? [
      {
        name: 'Platform Users',
        href: '/admin/users',
        icon: UserCog
      },
      {
        name: 'Organization Users',
        href: '/admin/organization-users',
        icon: Users
      }
    ] : []),
    // For org admins: show only their team
    ...(isOrgAdmin && !isPlatformAdmin ? [
      {
        name: 'Team',
        href: '/admin/team',
        icon: Users
      }
    ] : []),
    ...(isPlatformAdmin ? [
      {
        name: 'Full Assessment Access',
        href: '/admin/premium-requests',
        icon: Star,
        adminOnly: true
      }
    ] : []),
    {
      name: 'Organizations',
      href: '/admin/organizations',
      icon: Building,
      adminOnly: true
    }
  ];

  // Filter links based on user permissions
  const visibleLinks = links.filter(link => !link.adminOnly || isPlatformAdmin);

  return (
    <Sidebar className="border-r" collapsible="icon">
      <SidebarHeader className="border-b p-0">
        <div className="h-14 sm:h-16 flex items-center px-3 sm:px-4 lg:px-6">
          <Link to="/admin" className="flex items-center gap-2 min-w-0">
            <Logo className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 object-contain shrink-0" />
            {!isCollapsed && (
              <span className="text-sm sm:text-base lg:text-lg font-semibold truncate">
                Maturity Compass
              </span>
            )}
          </Link>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <nav className="space-y-1 p-2">
          {visibleLinks.map(link => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                "flex items-center px-3 py-3 text-sm rounded-md transition-colors group min-h-[48px] touch-manipulation",
                location.pathname === link.href
                  ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                  : "text-sidebar-foreground/60 hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
              )}
            >
              <link.icon className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span className="ml-3 truncate">{link.name}</span>}
            </Link>
          ))}
        </nav>
      </SidebarContent>

      <SidebarFooter>
        <UserProfileSection />
      </SidebarFooter>

      <button
        onClick={toggleSidebar}
        style={{ top: '50px' }}
        className={cn(
          "absolute bottom-4 -right-3 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow-sm transition-transform hover:bg-accent hidden md:flex touch-manipulation",
          isCollapsed && "rotate-180"
        )}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
    </Sidebar>
  );
}
